import { Injectable } from '@angular/core';
import { CarrinhoService } from '../carrinho.service';
import { ItemDePedidoConsultaDTO, PedidoDTO } from '../../models/pedidos/pedido.model';

/**
 * Converte um pedido em carrinho do PDV e deixa a venda amarrada a ele.
 *
 * Existe em um so lugar porque ha dois caminhos para o mesmo destino: o botao "Faturar" da tela de
 * Pedidos e o popup "Importar pedido" da tela de venda. Duplicar a conversao faria os dois
 * divergirem no primeiro ajuste de regra (observacao do item, variantes, saldo maximo).
 *
 * O transporte para o PDV e o cache `vendas_cache_{empresaId}` — nao ha router state: a tela de
 * venda le esse cache no `ngOnInit` (`restaurarCache`), inclusive o `pedidoId`, que depois viaja no
 * payload da nota e faz o backend baixar o saldo do pedido.
 */
@Injectable({ providedIn: 'root' })
export class ImportarPedidoParaVendaService {

  constructor(private carrinhoService: CarrinhoService) {}

  /**
   * Monta os itens do carrinho a partir dos itens do pedido.
   *
   * Fica de fora o item de cardapio sem correspondencia no catalogo (`item.item` nulo): o backend
   * exige `item_id` na nota, entao ele nao tem como ser faturado.
   */
  converterItens(pedido: PedidoDTO, itens: ItemDePedidoConsultaDTO[]): any[] {
    return itens
      .filter(item => !!item.item?.id)
      .map(item => ({
        item_id: item.item!.id,
        itemDePedidoId: item.id,
        nome: item.item!.nome || '',
        // A quantidade que vai para o carrinho e o SALDO, nao o pedido inteiro: o que ja foi
        // faturado antes nao pode ser cobrado de novo.
        quantidade: item.saldo,
        valorUnitario: item.valorUnitario,
        valorTotal: item.saldo * item.valorUnitario,
        valorTotalComDesconto: item.saldo * item.valorUnitario,
        custoUnitario: 0,
        custoTotal: 0,
        desconto: 0,
        tipoDeDesconto: 'SEM_DESCONTO',
        valorUnitarioComDesconto: 0,
        saldo: item.quantidade,
        saldoMaximoDoPedido: item.saldo,
        imagem: null,
        tipoDeImposto: null,
        // tipoItem nao esta disponivel no DTO de consulta de pedidos; MERCADORIA e o padrao seguro
        tipoItem: 'MERCADORIA',
        codigoItem: item.item!.codigoDeBarras || '',
        // Marca o item como vindo de pedido: o PDV limita a quantidade ao saldo do pedido, em vez
        // do estoque, e nao o remove na revalidacao de saldo.
        origemPedido: true,
        variantesDePreco: item.variantesDePreco || [],
        modificadores: item.modificadores || [],
        varianteDescricao: this.descreverVariantes(item),
        modificadoresDescricao: this.descreverModificadores(item),
        observacao: this.montarObservacaoItem(pedido.codigoDePedido, item),
        // A observacao do item do pedido ja chega preenchida e editavel no carrinho; no envio o PDV
        // junta com a `observacao` acima ("<manual> | <origem>").
        observacaoManual: item.observacao?.trim() || '',
      }));
  }

  /**
   * Coloca os itens no carrinho e grava o vinculo com o pedido no cache do PDV.
   *
   * @returns false quando nao sobrou nenhum item faturavel — o chamador avisa o usuario.
   */
  aplicarNoCarrinho(pedido: PedidoDTO, itens: ItemDePedidoConsultaDTO[]): boolean {
    const itensCarrinho = this.converterItens(pedido, itens);
    if (itensCarrinho.length === 0) return false;

    this.carrinhoService.clear();
    this.carrinhoService.setItems(itensCarrinho as any[]);

    try {
      const cacheKey = `vendas_cache_${this.obterEmpresaId()}`;
      const cacheExistente = localStorage.getItem(cacheKey);
      const cache = cacheExistente ? JSON.parse(cacheExistente) : {};
      cache.carrinho = itensCarrinho;
      cache.observacao = this.montarObservacaoVenda(pedido);
      cache.pedidoId = pedido.id;
      localStorage.setItem(cacheKey, JSON.stringify(cache));
    } catch { /* ignora erro de localStorage */ }

    return true;
  }

  /**
   * Observacao da venda: numero do pedido + a observacao geral do pedido, quando houver.
   * Formato: "Pedido #{numero} - {observacao}"
   */
  montarObservacaoVenda(pedido: PedidoDTO): string {
    const referencia = `Pedido #${pedido.codigoDePedido ?? ''}`;
    const observacao = pedido.observacao?.trim();
    return observacao ? `${referencia} - ${observacao}` : referencia;
  }

  /** Somente os itens que ainda tem algo a faturar. */
  itensComSaldo(itens: ItemDePedidoConsultaDTO[]): ItemDePedidoConsultaDTO[] {
    return itens.filter(item => (item.saldo || 0) > 0);
  }

  /**
   * Observacao de origem do item no carrinho: numero do pedido + variantes e modificadores. Partes
   * sem dados sao omitidas. A observacao digitada no pedido vai em `observacaoManual`, para nao
   * sair duplicada na nota.
   * Formato: "#Pedido {numero} - Variantes: {lista} - Modificadores: {lista}"
   */
  private montarObservacaoItem(codigoDePedido: number | undefined, item: ItemDePedidoConsultaDTO): string {
    const partes: string[] = [`#Pedido ${codigoDePedido ?? ''}`];

    const variantes = this.descreverVariantes(item);
    if (variantes) partes.push(`Variantes: ${variantes}`);

    const modificadores = this.descreverModificadores(item);
    if (modificadores) partes.push(`Modificadores: ${modificadores}`);

    return partes.join(' - ');
  }

  private descreverVariantes(item: ItemDePedidoConsultaDTO): string | null {
    if (!item.variantesDePreco || item.variantesDePreco.length === 0) return null;
    return item.variantesDePreco
      .map(v => this.descreverEscolha(v.descricao || v.codigoDaVariante || '', v.quantidade, item.quantidade))
      .filter(nome => nome)
      .join(', ') || null;
  }

  private descreverModificadores(item: ItemDePedidoConsultaDTO): string | null {
    if (!item.modificadores || item.modificadores.length === 0) return null;
    return item.modificadores
      .map(m => this.descreverEscolha(m.descricao || m.codigoDoModificador || '', m.quantidade, item.quantidade))
      .filter(nome => nome)
      .join(', ') || null;
  }

  /**
   * A quantidade da variante/modificador no pedido e o TOTAL da linha (por unidade x quantidade do
   * item). O "(xN)" mostra so o que vai em CADA unidade — a quantidade do item ja aparece na linha.
   */
  private descreverEscolha(nome: string, quantidade?: number, quantidadeDoItem?: number): string {
    const porUnidade = quantidadeDoItem ? Math.round((quantidade || 0) / quantidadeDoItem) : (quantidade || 0);
    return porUnidade > 1 ? `${nome} (x${porUnidade})` : nome;
  }

  private obterEmpresaId(): string {
    return sessionStorage.getItem('id') || 'semEmpresa';
  }
}
